// src/components/Auth/Register-Enhanced.js
// ENHANCED VERSION - Adds facility selection, Google OAuth, and profile picture
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  useTheme,
  ThemeProvider,
  createTheme,
  CssBaseline,
  alpha,
  styled,
  Grid,
  Fade,
  Zoom,
  InputAdornment,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  MedicalServices,
  Business,
  Badge,
  ArrowBack,
  Brightness4,
  Brightness7,
  Phone,
  CloudUpload,
  Google
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import FullLogo from '../logo/FullLogo';

// Keep all existing Tesla colors and styled components
const teslaColors = {
  dark: {
    primary: '#3E6AE1',
    secondary: '#34C3FF',
    background: '#121212',
    surface: '#1E1E1E',
    card: 'rgba(30, 30, 30, 0.8)',
    text: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.7)',
    border: 'rgba(255, 255, 255, 0.12)',
  },
  light: {
    primary: '#3E6AE1',
    secondary: '#34C3FF',
    background: '#F5F5F7',
    surface: '#FFFFFF',
    card: 'rgba(255, 255, 255, 0.8)',
    text: '#121212',
    textSecondary: 'rgba(18, 18, 18, 0.7)',
    border: 'rgba(0, 0, 0, 0.12)',
  }
};

// Simplified card component
const AuthCard = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'rgba(30, 30, 30, 0.95)' 
    : 'rgba(255, 255, 255, 0.95)',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.12)' 
    : 'rgba(0, 0, 0, 0.12)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 6px rgba(0, 0, 0, 0.1)'
    : '0 4px 6px rgba(0, 0, 0, 0.05)',
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: '564px',
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  background: '#0067BC',
  border: 0,
  borderRadius: '6px',
  color: 'white',
  padding: '10px 16px',
  fontWeight: 600,
  fontSize: '16px',
  textTransform: 'none',
  '&:hover': {
    background: '#0055A3',
  },
  '&:disabled': {
    background: '#A9C7DF',
    color: 'white',
    cursor: 'not-allowed',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(0, 0, 0, 0.02)',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0067BC',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0067BC',
      borderWidth: '1px',
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: '6px',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(0, 0, 0, 0.02)',
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0067BC',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0067BC',
    borderWidth: '1px',
  },
}));

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: { main: teslaColors.light.primary },
          secondary: { main: teslaColors.light.secondary },
          background: { default: teslaColors.light.background, paper: teslaColors.light.surface },
          text: { primary: teslaColors.light.text, secondary: teslaColors.light.textSecondary },
          divider: teslaColors.light.border,
        }
      : {
          primary: { main: teslaColors.dark.primary },
          secondary: { main: teslaColors.dark.secondary },
          background: { default: teslaColors.dark.background, paper: teslaColors.dark.surface },
          text: { primary: teslaColors.dark.text, secondary: teslaColors.dark.textSecondary },
          divider: teslaColors.dark.border,
        }),
  },
  typography: {
    fontFamily: '"Satoshi", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 600 },
    body1: { fontWeight: 400, lineHeight: 1.6 },
    body2: { fontWeight: 400 },
  },
  shape: { borderRadius: 14 },
});

export const ColorModeContext = React.createContext({ toggleColorMode: () => {} });

const USER_MANAGEMENT_ROLES = [
  { value: 'facility_admin', label: 'Facility Admin' },
  { value: 'in_house_radiologist', label: 'In-House Radiologist (DycoVue Reader Portal)' },
  { value: 'radiologist', label: 'Radiologist' },
  { value: 'clinician', label: 'Clinician' },
  { value: 'super_clinician', label: 'Super Clinician' },
  { value: 'technologist', label: 'Technologist' },
  { value: 'radiographer', label: 'Radiographer' },
  { value: 'sonographer', label: 'Sonographer' },
  { value: 'referring_physician', label: 'Referring Physician' },
  { value: 'nurse', label: 'Nurse' },
  { value: 'medical_records_officer', label: 'Medical Records Officer' },
  { value: 'lab_admin', label: 'Laboratory Admin (LIS)' },
  { value: 'lab_scientist', label: 'Medical Lab Scientist (LIS)' },
  { value: 'senior_lab_scientist', label: 'Sr. Lab Scientist (LIS)' },
  { value: 'pathologist', label: 'Pathologist (LIS)' },
  { value: 'histotechnologist', label: 'Histotechnologist (LIS)' },
  { value: 'cytotechnologist', label: 'Cytotechnologist (LIS)' },
  { value: 'microbiologist', label: 'Microbiologist (LIS)' },
  { value: 'phlebotomist', label: 'Phlebotomist (LIS)' },
  { value: 'it_admin', label: 'IT Admin' },
  { value: 'tech_support', label: 'Tech Support' },
  { value: 'product_lead', label: 'Product Lead' },
  { value: 'customer_success', label: 'Customer Success' },
  { value: 'compliance_officer', label: 'Compliance Officer' },
];

function RegisterEnhanced() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isGoogleAuth = searchParams.get('google') === 'true';
  
  // NEW: Enhanced state with facility and phone
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '', // NEW
    specialty: '',
    hospital: '', // Keep for backward compatibility
    facility: '', // NEW: Required
    institutionRole: '', // NEW: Optional
    role: '',
  });
  
  // NEW: Facility management
  const [facilities, setFacilities] = useState([]);
  const [loadingFacilities, setLoadingFacilities] = useState(true);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { mode, toggleTheme: sharedToggleTheme } = useThemeMode();

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: sharedToggleTheme,
    }),
    [sharedToggleTheme],
  );

  const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  // NEW: Fetch facilities on mount
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const response = await api.get('/facilities/public/list');
        setFacilities(response.data.facilities || []);
      } catch (err) {
        console.error('Failed to fetch facilities:', err);
        setError('Failed to load facilities. Please refresh the page.');
      } finally {
        setLoadingFacilities(false);
      }
    };

    fetchFacilities();
  }, []);

  // NEW: Handle Google OAuth if coming from Google
  useEffect(() => {
    if (isGoogleAuth) {
      // Google profile might be in session storage
      const googleProfile = sessionStorage.getItem('googleProfile');
      if (googleProfile) {
        try {
          const profile = JSON.parse(googleProfile);
          setUserData(prev => ({
            ...prev,
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            email: profile.email || '',
          }));
          sessionStorage.removeItem('googleProfile'); // Clean up
        } catch (err) {
          console.error('Failed to parse Google profile:', err);
        }
      }
    }
  }, [isGoogleAuth]);

  const handleChange = (e) => {
    setUserData({
      ...userData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Password validation (skip if Google OAuth)
    if (!isGoogleAuth && userData.password !== userData.confirmPassword) {
      setError("Passwords don't match");
      setLoading(false);
      return;
    }

    // Facility validation
    if (!userData.facility) {
      setError('Please select a facility');
      setLoading(false);
      return;
    }

    try {
      const registrationData = {
        firstName: userData.firstName.trim(),
        lastName: userData.lastName.trim(),
        email: userData.email.trim().toLowerCase(),
        phoneNumber: userData.phoneNumber,
        facility: userData.facility,
        institutionRole: userData.institutionRole,
        role: userData.role || undefined,
        specialty: userData.specialty,
      };

      // Add password only if not Google OAuth
      if (!isGoogleAuth) {
        registrationData.password = userData.password;
      }

      const response = await api.post('/auth/register', registrationData);
      
      // Check approval status
      if (response.data.user.approvalStatus === 'pending') {
        setSuccess(response.data.message || 'Registration successful! Your account is pending approval. You will receive an email once approved.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // Auto-approved (unlikely in new system)
        authLogin(response.data.user, response.data.token);
        navigate('/clinician');
      }
    } catch (err) {
      const baseError = err.response?.data?.error || err.response?.data?.message || 'Registration failed. Please try again.';
      const details = err.response?.data?.details;
      if (details && details.length > 0) {
        const fieldErrors = details.map(d => d.msg).join(', ');
        setError(`${baseError}: ${fieldErrors}`);
      } else {
        setError(baseError);
      }
    } finally {
      setLoading(false);
    }
  };

  // NEW: Google Sign-In handler
  const handleGoogleSignIn = () => {
    // Redirect to backend Google OAuth endpoint
    window.location.href = `${process.env.REACT_APP_API_URL || ''}/api/auth/google`;
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, px: 2 }}>
          <AuthCard>
            {/* Logo */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <FullLogo />
            </Box>

            {/* Welcome Text */}
            <Box sx={{ mb: 4 }}>
              <Typography
                component="h1"
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#111827',
                }}
              >
                {t('auth.registration')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ 
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#6B7280',
                  fontSize: '0.875rem',
                }}
              >
                Create your account to get started
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: '6px' }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3, borderRadius: '6px' }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <StyledTextField 
                    required 
                    fullWidth 
                    id="firstName" 
                    label={t('profile.firstName')} 
                    name="firstName" 
                    autoComplete="given-name" 
                    autoFocus={!isGoogleAuth} 
                    value={userData.firstName} 
                    onChange={handleChange}
                    placeholder="Enter your first name"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField 
                    required 
                    fullWidth 
                    id="lastName" 
                    label={t('profile.lastName')} 
                    name="lastName" 
                    autoComplete="family-name" 
                    value={userData.lastName} 
                    onChange={handleChange}
                    placeholder="Enter your last name"
                  />
                </Grid>
              </Grid>

              <StyledTextField 
                margin="normal" 
                required 
                fullWidth 
                id="email" 
                label={t('auth.emailAddress')} 
                name="email" 
                autoComplete="email" 
                type="email" 
                value={userData.email} 
                onChange={handleChange} 
                disabled={isGoogleAuth}
                placeholder="Enter your Email address"
              />

              <StyledTextField 
                margin="normal" 
                fullWidth 
                id="phoneNumber" 
                label={t('profile.phone')} 
                name="phoneNumber" 
                autoComplete="tel" 
                value={userData.phoneNumber} 
                onChange={handleChange}
                placeholder="Enter your mobile number"
              />

              <FormControl fullWidth margin="normal" required>
                <InputLabel id="facility-label">{t('auth.facilityInstitution')}</InputLabel>
                <StyledSelect 
                  labelId="facility-label" 
                  id="facility" 
                  name="facility" 
                  value={userData.facility} 
                  label={t('auth.facilityInstitution')} 
                  onChange={handleChange} 
                  disabled={loadingFacilities}
                >
                  {loadingFacilities ? (
                    <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> {t('auth.loadingFacilities')}</MenuItem>
                  ) : facilities.length === 0 ? (
                    <MenuItem disabled>{t('auth.noActiveFacilities')}</MenuItem>
                  ) : (
                    facilities.map((facility) => (
                      <MenuItem key={facility._id} value={facility._id}>
                        {facility.facilityName} {facility.address?.city ? `(${facility.address.city})` : ''}
                      </MenuItem>
                    ))
                  )}
                </StyledSelect>
              </FormControl>

              <FormControl fullWidth margin="normal" required>
                <InputLabel id="specialty-label">{t('auth.medicalSpecialty')}</InputLabel>
                <StyledSelect 
                  labelId="specialty-label" 
                  id="specialty" 
                  name="specialty" 
                  value={userData.specialty} 
                  label={t('auth.medicalSpecialty')} 
                  onChange={handleChange}
                >
                  <MenuItem value="Neuroradiology">Neuroradiology</MenuItem>
                  <MenuItem value="Chest Radiology">Chest Radiology</MenuItem>
                  <MenuItem value="Cardiac Imaging">Cardiac Imaging</MenuItem>
                  <MenuItem value="Abdominal Imaging">Abdominal Imaging</MenuItem>
                  <MenuItem value="Musculoskeletal Radiology">Musculoskeletal Radiology</MenuItem>
                  <MenuItem value="Breast Imaging">Breast Imaging</MenuItem>
                  <MenuItem value="Pediatric Radiology">Pediatric Radiology</MenuItem>
                  <MenuItem value="Interventional Radiology">Interventional Radiology</MenuItem>
                  <MenuItem value="Nuclear Medicine">Nuclear Medicine</MenuItem>
                  <MenuItem value="General Radiology">General Radiology</MenuItem>
                </StyledSelect>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel id="institutionRole-label">{t('auth.rolePositionOptional') || 'Role / Position'}</InputLabel>
                <StyledSelect
                  labelId="institutionRole-label"
                  id="institutionRole"
                  name="institutionRole"
                  value={userData.institutionRole}
                  label={t('auth.rolePositionOptional') || 'Role / Position'}
                  onChange={(e) => {
                    const selectedVal = e.target.value;
                    const matchedRole = USER_MANAGEMENT_ROLES.find(r => r.label === selectedVal || r.value === selectedVal);
                    setUserData(prev => ({
                      ...prev,
                      institutionRole: selectedVal,
                      role: matchedRole ? matchedRole.value : '',
                    }));
                  }}
                >
                  <MenuItem value=""><em>None / Not Specified</em></MenuItem>
                  {USER_MANAGEMENT_ROLES.map((role) => (
                    <MenuItem key={role.value} value={role.label}>
                      {role.label}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>

              {!isGoogleAuth && (
                <>
                  <StyledTextField 
                    margin="normal" 
                    required 
                    fullWidth 
                    name="password" 
                    label={t('auth.password')} 
                    type={showPassword ? 'text' : 'password'} 
                    id="password" 
                    autoComplete="new-password" 
                    value={userData.password} 
                    onChange={handleChange}
                    placeholder="Enter your password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={handleClickShowPassword} 
                            edge="end" 
                            sx={{ color: 'text.secondary' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <StyledTextField 
                    margin="normal" 
                    required 
                    fullWidth 
                    name="confirmPassword" 
                    label={t('profile.security.confirmPassword')} 
                    type={showConfirmPassword ? 'text' : 'password'} 
                    id="confirmPassword" 
                    autoComplete="new-password"
                    value={userData.confirmPassword} 
                    onChange={handleChange}
                    placeholder="Enter confirm password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={handleClickShowConfirmPassword} 
                            edge="end" 
                            sx={{ color: 'text.secondary' }}
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}

              <PrimaryButton 
                type="submit" 
                fullWidth 
                sx={{ mt: 3, mb: 2, py: 1.5 }} 
                disabled={loading || loadingFacilities}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: 'white' }} />
                    <span>Submitting...</span>
                  </Box>
                ) : (
                  'Create Account'
                )}
              </PrimaryButton>

              <Box textAlign="center" sx={{ mt: 3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#6B7280',
                    fontSize: '0.875rem',
                  }}
                >
                  Already have an account?{' '}
                  <Link 
                    to="/login" 
                    style={{ 
                      color: '#0067BC', 
                      textDecoration: 'none', 
                      fontWeight: 600 
                    }}
                  >
                    Sign In
                  </Link>
                </Typography>
              </Box>
            </form>
          </AuthCard>
        </Box>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default RegisterEnhanced;